'use client'

import type {
  CartItem,
  CategoryDTO,
  CheckoutProduct,
  Currency,
  Option,
  ProductLightDTO
} from 'ecosystem'
import { sendGTMEvent } from '@next/third-parties/google'
import { getProductPrice } from 'shared-utils'
import {
  createCategoryMap,
  ga4EcommerceItemFactoryFromCartItems,
  ga4EcommerceItemsFactory
} from '../../data-collection/utils'
import { GA4_ECOMMERCE_EVENTS } from '../../data-collection/constants'
import { type GaPurchaseOrder } from './types'

/**
 * Check if an event is an ecommerce event that requires clearing
 */
const isEcommerceEvent = (eventName: string, params: any): boolean => {
  // Check if it's a standard GA4 ecommerce event
  if (GA4_ECOMMERCE_EVENTS.includes(eventName as any)) {
    return true
  }

  // Check if the event contains ecommerce data
  return typeof params === 'object' && 'ecommerce' in params
}

/**
 * Clear ecommerce object to prevent data pollution
 * This should be called before pushing any ecommerce event
 */
const clearEcommerceParams = () => {
  sendGTMEvent({ ecommerce: null })
}

const useGoogleAnalytics = function () {
  const trackEvent = (eventName: string, eventData: Record<string, any>): boolean => {
    try {
      if (isEcommerceEvent(eventName, eventData)) {
        clearEcommerceParams()
      }

      sendGTMEvent({ event: eventName, ...eventData })
      return true
    } catch (e) {
      console.warn(`Analytics - could not track event: ${eventName}`, e)
      return false
    }
  }

  // ******************* GOOGLE ANALYTICS API - COMMON *****************

  const pageView = (url: string) => trackEvent('page_view', { page_path: url })

  // ******************* STENLUNDS PROFESSIONAL ONLY ***********************

  const selectContent = (content: { content_type: string; item_id: string }) =>
    trackEvent('select_content', content)

  const contactFormSubmit = (pagePath: string) => {
    if (typeof window !== 'undefined') {
      return trackEvent('contact_form_submit', { page_path: pagePath })
    }
    return false
  }

  // ******************* GA4 ECOMMERCE ITEM FACTORY ***********************
  /* Matches GA4 Ecommerce Data Structure (more fields can be added)
   https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
   */
  const ecommerceItemsFactoryFromProducts = (
    products: ProductLightDTO[],
    categories: Option<CategoryDTO[]>
  ) => {
    const categoryMap = createCategoryMap(categories)

    return ga4EcommerceItemsFactory(
      products.map((product) => ({
        productId: product.id,
        articleNumber: product.articleNumber,
        name: product.name,
        brand: product.brand?.name,
        unitPrice: getProductPrice(product),
        quantity: 1,
        categoryIds: product.categoryIds ?? undefined
      })),
      categoryMap
    )
  }

  // ******************* GOOGLE ANALYTICS API - ECOMMERCE *****************

  /**
   * Track view_item_list event
   * @param products - List of items being viewed
   * @param listId - Optional ID of the list
   * @param listName - Optional name of the list
   * @param categories - Product categories for mapping
   * @param currency - Currency for the transaction
   */
  const viewItemList = (
    products: ProductLightDTO[],
    listId: string | undefined,
    listName: string | undefined,
    categories: Option<CategoryDTO[]>,
    currency: Currency
  ) => {
    const ecommerceItems = ecommerceItemsFactoryFromProducts(products, categories)

    const enrichedItems = ecommerceItems.map((item) => ({
      ...item,
      ...(listId && { item_list_id: listId }),
      ...(listName && { item_list_name: listName })
    }))

    return trackEvent('view_item_list', {
      ecommerce: {
        currency,
        items: enrichedItems
      }
    })
  }

  /**
   * Track view_item event (product detail view)
   * @param product - Item being viewed
   * @param categories - Product categories for mapping
   * @param currency - Currency for the transaction
   */
  const viewItem = (
    product: ProductLightDTO,
    categories: Option<CategoryDTO[]>,
    currency: Currency
  ) =>
    trackEvent('view_item', {
      ecommerce: {
        currency,
        value: getProductPrice(product),
        items: ecommerceItemsFactoryFromProducts([product], categories)
      }
    })

  /**
   * Track add_to_cart or remove_from_cart events
   * @param action - 'add' or 'remove'
   * @param cartTotal - Total value of the items
   * @param cartItems - List of items added/removed
   * @param categories - Product categories for mapping
   * @param currency - Currency for the transaction
   */
  const updateCart = (
    action: 'add' | 'remove',
    cartTotal: number,
    cartItems: CartItem[],
    categories: Option<CategoryDTO[]>,
    currency: Currency
  ) => {
    const eventName = action === 'add' ? 'add_to_cart' : 'remove_from_cart'
    const categoryMap = createCategoryMap(categories)

    return trackEvent(eventName, {
      ecommerce: {
        currency,
        value: cartTotal,
        items: ga4EcommerceItemFactoryFromCartItems(cartItems, categoryMap)
      }
    })
  }

  /**
   * Track view_cart event
   * @param cartItems - Items in cart
   * @param cartTotal - Total cart value
   * @param categories - Product categories for mapping
   * @param currency - Currency for the transaction
   */
  const viewCart = (
    cartItems: CartItem[],
    cartTotal: number,
    categories: Option<CategoryDTO[]>,
    currency: Currency
  ) => {
    const categoryMap = createCategoryMap(categories)

    return trackEvent('view_cart', {
      ecommerce: {
        currency,
        value: cartTotal,
        items: ga4EcommerceItemFactoryFromCartItems(cartItems, categoryMap)
      }
    })
  }

  /**
   * Track begin_checkout event
   * @param cartItems - Items in checkout
   * @param cartTotal - Total checkout value
   * @param categories - Product categories for mapping
   * @param currency - Currency for the transaction
   */
  const checkout = (
    cartItems: CartItem[],
    cartTotal: number,
    categories: Option<CategoryDTO[]>,
    currency: Currency
  ) => {
    const categoryMap = createCategoryMap(categories)

    return trackEvent('begin_checkout', {
      ecommerce: {
        currency,
        value: cartTotal,
        items: ga4EcommerceItemFactoryFromCartItems(cartItems, categoryMap)
      }
    })
  }

  /**
   * Track purchase event
   * @param order - Order information
   * @param items - Products in the order
   * @param categories - Product categories for mapping
   * @param currency - Currency for the transaction
   */
  const purchase = (
    order: GaPurchaseOrder,
    items: CheckoutProduct[],
    categories: Option<CategoryDTO[]>,
    currency: Currency
  ) => {
    const categoryMap = createCategoryMap(categories)

    return trackEvent('purchase', {
      ecommerce: {
        // Custom properties
        order_id: order.id,
        discount_total: order.discountAmount,
        shipping_method: order.shippingMethod,
        payment_method: order.paymentMethod,

        // GA4 recommended properties
        transaction_id: order.id,
        currency: order.currency ?? currency,
        value: order.totalAmount ?? 0,
        tax: order.taxAmount,
        shipping: order.shippingPrice,
        coupon: order.discountCode,
        items: ga4EcommerceItemsFactory(items, categoryMap)
      }
    })
  }

  // ******************* GOOGLE ANALYTICS API - SEARCH *****************

  const search = (searchTerm: string) => trackEvent('search', { search_term: searchTerm })

  const viewPdp = (pagePath: string) => trackEvent('view_pdp', { page_path: pagePath })

  return {
    // Common events
    pageView,
    contactFormSubmit,
    selectContent,

    // E-commerce events
    viewItemList,
    viewItem,
    updateCart,
    viewCart,
    checkout,
    purchase,

    // Search events
    search,
    viewPdp
  }
}

export default useGoogleAnalytics
